// src/components/common/MluDetails.js
// Click an MLU code anywhere to read its catalogue description.
import React, { createContext, useCallback, useContext, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link, Stack, Typography,
} from "@mui/material";
import { getCatalogEntry } from "../../api";

const MluContext = createContext(() => {});

export function MluDetailsProvider({ children }) {
  const [id, setId] = useState(null);
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);
  const open = useCallback((mluId) => {
    setId(mluId);
    setItem(null);
    setError(null);
    getCatalogEntry(mluId).then((r) => setItem(r.item)).catch((e) => setError(e.message));
  }, []);
  const Section = ({ title, text }) => (text ? (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="subtitle2">{title}</Typography>
      <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>{text}</Typography>
    </Box>
  ) : null);
  return (
    <MluContext.Provider value={open}>
      {children}
      <Dialog open={!!id} onClose={() => setId(null)} maxWidth="md" fullWidth aria-labelledby="mlu-title">
        <DialogTitle id="mlu-title" sx={{ fontWeight: 700 }}>{item ? `${item.id}: ${item.title}` : id}</DialogTitle>
        <DialogContent>
          {!item && !error && <CircularProgress size={24} />}
          {error && <Alert severity="error">{error}</Alert>}
          {item && (
            <>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
                <Chip size="small" label={item.framework} />
                {item.module && <Chip size="small" variant="outlined" label={item.module} />}
                {item.level && <Chip size="small" variant="outlined" label={`Level: ${item.level}`} />}
                {item.eqf && <Chip size="small" variant="outlined" label={`EQF ${item.eqf}`} />}
              </Stack>
              <Section title="In short" text={item.intro_short} />
              <Section title="Learning outcome" text={item.outcome} />
              <Section title="Description" text={item.intro_extended} />
              <Section title="Prerequisites" text={item.prereq} />
              <Section title="Knowledge" text={item.knowledge} />
              <Section title="Skills" text={item.skills} />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setId(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </MluContext.Provider>
  );
}

export function useOpenMlu() {
  return useContext(MluContext);
}

export function MluLink({ id, children }) {
  const open = useOpenMlu();
  return (
    <Link component="button" type="button" variant="inherit" onClick={(e) => { e.stopPropagation(); open(id); }}
      sx={{ fontWeight: 600, verticalAlign: "baseline" }} aria-label={`Read the description of ${id}`}>
      {children || id}
    </Link>
  );
}
